/**
 * Status-bar item of the Lines feature (W2-07): a chip with the active line ("#3 Arm L", its type icon) that opens
 * the line switcher, and the host of the feature's own dialogs (line switcher and pickers, New N lines, picture and
 * HUD element pickers, groups editor). The host lives in the editor's hint bar, so these dialogs work whether or not
 * the Lines tab is visible (commands from the palette and the keyboard open them too).
 */

import type { AppServices } from '../../app/registry/types';
import { useT } from '../../i18n/useT';
import type { DocStore } from '../../state/types';
import { Icon } from '../../ui/Icon';
import { Tooltip } from '../../ui/Tooltip';
import { GroupsEditor } from './GroupsEditor';
import { useDocValue, useEditorValue, useLinesUi, useServicesStore } from './hooks';
import { lineTitle } from './lineActions';
import { openSwitcher } from './lineCommands';
import { m } from './lines.messages';
import { buildLineTable, TYPE_ICONS, UI_ELEMENT_ICONS } from './lineModel';
import { LineSwitcher } from './LineSwitcher';
import { linesUi, type LinesModal } from './linesUi';
import { ImagePickerDialog, NewLinesDialog, UiElementDialog } from './NewLineDialogs';
import s from './Lines.module.css';

const TOPICS = ['lines', 'lineOrder', 'tracks', 'keys', 'notes', 'groups', 'assets'] as const;

function ActiveLineChip(props: { readonly services: AppServices; readonly store: DocStore }) {
  const { services, store } = props;
  const t = useT();
  const active = useEditorValue(services, (st) => st.activeLine);
  const table = useDocValue(store, buildLineTable, TOPICS);
  const info = active === null ? undefined : table.byId.get(active);
  const label = info === undefined ? t(m.noActiveLine) : lineTitle(store, info.id);
  const icon =
    info === undefined
      ? 'ListTree'
      : info.uiElement !== null
        ? UI_ELEMENT_ICONS[info.uiElement]
        : TYPE_ICONS[info.type];
  return (
    <Tooltip content={t(m.activeChipTip)} command="lines.switcher" side="top">
      <button
        type="button"
        className={s.activeChip}
        aria-label={t(m.activeChipLabel, { line: label })}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={() => {
          openSwitcher(services);
        }}
      >
        <Icon name={icon} size={12} />
        <span className={s.activeChipText}>{label}</span>
      </button>
    </Tooltip>
  );
}

function ModalView(props: { readonly services: AppServices; readonly store: DocStore; readonly modal: LinesModal }) {
  const { services, store, modal } = props;
  const close = (): void => {
    linesUi(services).set({ modal: null });
  };
  switch (modal.kind) {
    case 'picker':
      return <LineSwitcher services={services} store={store} mode={modal.mode} onClose={close} />;
    case 'newLines':
      return <NewLinesDialog services={services} store={store} preset={modal.preset} onClose={close} />;
    case 'groups':
      return <GroupsEditor services={services} store={store} onClose={close} />;
    case 'image':
      return <ImagePickerDialog services={services} store={store} gif={modal.gif} onClose={close} />;
    case 'uiElement':
      return <UiElementDialog services={services} onClose={close} />;
  }
}

export function LinesHost(props: { readonly services: AppServices }) {
  const { services } = props;
  const store = useServicesStore(services);
  const modal = useLinesUi(services, (st) => st.modal);
  if (store === null) return null;
  return (
    <>
      <ActiveLineChip services={services} store={store} />
      {modal !== null && (
        <ModalView
          key={modal.kind === 'picker' ? `picker:${modal.mode.kind}` : modal.kind}
          services={services}
          store={store}
          modal={modal}
        />
      )}
    </>
  );
}
